const designElements = [
  { label: "Wood tone", detail: "Light oak, ash, or pale walnut — warm but not dark, so it contrasts gently with a beige sofa without overpowering the palette." },
  { label: "Shape", detail: "Round or oval softens a room and works beautifully with curved sofas; rectangular suits longer, more structured spaces." },
  { label: "Height", detail: "Roughly the same height as the sofa seat cushion — around 40–45cm — for comfortable reach and visual balance." },
  { label: "Finish", detail: "Matte or oiled finish reads as more natural and calm than high-gloss lacquer, which can feel cold and formal." },
  { label: "Storage", detail: "A lower shelf adds practical storage without adding visual weight — ideal for books, trays, and baskets." },
  { label: "Scale", detail: "Leave 40–45cm between the table and the sofa — enough to reach comfortably without crowding the seating area." },
];

const stylingTips = [
  "Use a wooden tray to corral objects on the table — it makes the styling look intentional and keeps things from feeling scattered.",
  "Limit the table to three or four objects: a tray, a small ceramic vase, a candle, and one or two books is all you need.",
  "In a calm room, negative space is part of the design — resist the urge to fill every inch of the table.",
  "Choose objects in the same warm neutral palette as the room: cream, sand, warm white, and natural wood tones.",
  "A small plant or dried botanicals add life and organic texture without introducing colour.",
  "Vary the heights of objects slightly — a tall vase next to a low candle creates a more considered, editorial look.",
  "Avoid shiny or reflective objects in a calm room — matte ceramics, raw wood, and natural stone all work better.",
];

export default function WCTContent() {
  return (
    <article className="max-w-2xl mx-auto px-4 md:px-0 py-16">

      <div className="mb-14">
        <p className="text-[#B5A898] text-[10px] tracking-[0.45em] uppercase mb-4" style={{ fontFamily: "'Inter', sans-serif" }}>Introduction</p>
        <p className="text-[#4A3F38] text-base md:text-lg leading-relaxed mb-5" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 400 }}>
          A light wood coffee table is one of the most versatile pieces you can add to a calm living room.
        </p>
        <p className="text-[#6B5E53] text-sm md:text-base leading-relaxed" style={{ fontFamily: "'Inter', sans-serif", fontWeight: 300 }}>
          It adds warmth, natural texture, and a gentle contrast to a soft beige or neutral palette — without demanding attention.
          When chosen well, a light wood table works across styles and seasons, pairing naturally with linen sofas, neutral rugs,
          and soft curtains. This guide covers what to look for, how to style it, and which picks we keep returning to.
        </p>
      </div>

      <div className="mb-14">
        <p className="text-[#B5A898] text-[10px] tracking-[0.45em] uppercase mb-4" style={{ fontFamily: "'Inter', sans-serif" }}>Why It Works</p>
        <h2 className="text-[#2C2825] text-2xl md:text-3xl leading-snug mb-6" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 400 }}>
          Why Light Wood Works Better Than Dark in a Neutral Room
        </h2>
        <div className="w-12 h-px bg-[#C8BAA8] mb-8" />

        <div className="relative overflow-hidden rounded-2xl mb-8">
          <img
            src="https://public.readdy.ai/ai/img_res/739e9087b4946417310ae078d6d3eb98.jpg"
            alt="Light wood coffee table grain detail"
            className="w-full h-[280px] md:h-[360px] object-cover object-top"
          />
        </div>

        <p className="text-[#6B5E53] text-sm md:text-base leading-relaxed mb-5" style={{ fontFamily: "'Inter', sans-serif", fontWeight: 300 }}>
          Dark wood can feel heavy in a calm neutral room — it draws the eye downward and can make the space feel smaller and more enclosed.
          Light oak, ash, and pale walnut, by contrast, add warmth and natural texture without visual weight. They reflect light gently,
          complement a beige sofa beautifully, and feel at home in both minimal and layered interiors.
        </p>
        <p className="text-[#6B5E53] text-sm md:text-base leading-relaxed" style={{ fontFamily: "'Inter', sans-serif", fontWeight: 300 }}>
          The grain of light wood also adds a layer of natural interest that no painted or lacquered surface can replicate.
          In a room built on texture and warmth, a well-chosen wood table is one of the most quietly impactful pieces you can invest in.
          It grounds the seating area, adds a natural focal point, and ties together the other warm elements in the room.
        </p>
      </div>

      <div className="mb-14">
        <p className="text-[#B5A898] text-[10px] tracking-[0.45em] uppercase mb-4" style={{ fontFamily: "'Inter', sans-serif" }}>What to Look For</p>
        <h2 className="text-[#2C2825] text-2xl md:text-3xl leading-snug mb-6" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 400 }}>
          Key Things to Consider When Choosing a Coffee Table
        </h2>
        <div className="w-12 h-px bg-[#C8BAA8] mb-8" />
        <div className="space-y-4">
          {designElements.map((el, i) => (
            <div key={i} className="flex gap-5 items-start border-b border-[#EDE6DA] pb-4 last:border-0">
              <span className="text-[#B5A898] text-[10px] tracking-[0.3em] uppercase w-24 flex-shrink-0 pt-0.5" style={{ fontFamily: "'Inter', sans-serif" }}>{el.label}</span>
              <p className="text-[#6B5E53] text-sm leading-relaxed" style={{ fontFamily: "'Inter', sans-serif", fontWeight: 300 }}>{el.detail}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="mb-14">
        <p className="text-[#B5A898] text-[10px] tracking-[0.45em] uppercase mb-4" style={{ fontFamily: "'Inter', sans-serif" }}>Styling</p>
        <h2 className="text-[#2C2825] text-2xl md:text-3xl leading-snug mb-6" style={{ fontFamily: "'Playfair Display', serif", fontWeight: 400 }}>
          How to Style a Light Wood Coffee Table
        </h2>
        <div className="w-12 h-px bg-[#C8BAA8] mb-8" />

        <div className="relative overflow-hidden rounded-2xl mb-8">
          <img
            src="https://readdy.ai/api/search-image?query=light%20oak%20coffee%20table%20styled%20with%20ceramic%20vase%20wooden%20tray%20candle%20books%20calm%20living%20room%20warm%20natural%20light%20editorial%20interior%20photography%20neutral%20palette&width=900&height=500&seq=wct_style1&orientation=landscape"
            alt="Styled light wood coffee table in a calm room"
            className="w-full h-[280px] md:h-[360px] object-cover object-top"
          />
        </div>

        <ul className="space-y-4">
          {stylingTips.map((tip, i) => (
            <li key={i} className="flex gap-4 items-start">
              <span className="text-[#C8BAA8] text-[10px] tracking-[0.3em] mt-1 flex-shrink-0" style={{ fontFamily: "'Inter', sans-serif" }}>0{i + 1}</span>
              <p className="text-[#6B5E53] text-sm leading-relaxed" style={{ fontFamily: "'Inter', sans-serif", fontWeight: 300 }}>{tip}</p>
            </li>
          ))}
        </ul>
      </div>

    </article>
  );
}
